/**
 * Advanced Transcription Testing Component
 * Tests live transcription, file upload with diarization, and speaker identification
 */
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useAuth } from "../contexts/AuthContext";

const DEFAULT_MODEL_ID = "whisper-large-v3-turbo";
const DEFAULT_MODEL_LABEL = "Whisper Large V3 Turbo";
const DEFAULT_MODEL_DESCRIPTION =
  "OpenAI Whisper large-v3 Turbo (CTranslate2, float16).";
const FALLBACK_MODELS = [
  {
    id: DEFAULT_MODEL_ID,
    label: DEFAULT_MODEL_LABEL,
    description: DEFAULT_MODEL_DESCRIPTION,
    backend: "faster-whisper",
    available: true,
  },
  {
    id: "whisper-large-v3-french",
    label: "Whisper Large V3 French",
    description:
      "Whisper large-v3 fine-tuné pour la dictée médicale en français.",
    backend: "faster-whisper",
    available: true,
    live_capable: true,
  },
];

const formatSeconds = (value) => {
  const numericValue =
    typeof value === "string" ? Number.parseFloat(value) : value;
  if (typeof numericValue !== "number" || Number.isNaN(numericValue)) {
    return "—";
  }
  return `${numericValue.toFixed(2)}s`;
};

const AdvancedTranscriptionTesting = () => {
  const { user } = useAuth();
  const [apiToken, setApiToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableModels, setAvailableModels] = useState(FALLBACK_MODELS);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelFetchError, setModelFetchError] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState(DEFAULT_MODEL_ID);

  // Live Transcription (WebSocket)
  const [isRecording, setIsRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [wsConnection, setWsConnection] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Live Transcription Options
  const [liveDiarization, setLiveDiarization] = useState(false);
  const [liveUseSignatures, setLiveUseSignatures] = useState(false);
  const [liveVoiceSimilarityThreshold, setLiveVoiceSimilarityThreshold] =
    useState("0.30");
  const [
    liveAnonymousSimilarityThreshold,
    setLiveAnonymousSimilarityThreshold,
  ] = useState("0.55");

  // File Upload Testing
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUseSignatures, setFileUseSignatures] = useState(false);
  const [fileVoiceSimilarityThreshold, setFileVoiceSimilarityThreshold] =
    useState("0.30");
  const [
    fileAnonymousSimilarityThreshold,
    setFileAnonymousSimilarityThreshold,
  ] = useState("0.55");
  const [uploadResults, setUploadResults] = useState(null);

  // Voice Signature Generation Testing
  const [signatureAudioFiles, setSignatureAudioFiles] = useState([]);
  const [signatureSpeakerNames, setSignatureSpeakerNames] = useState({});
  const [generatedSignatures, setGeneratedSignatures] = useState(null);
  const [generatingSignatures, setGeneratingSignatures] = useState(false);
  const [selectedSignatureIndices, setSelectedSignatureIndices] = useState([]);

  const generatedSignatureList = Array.isArray(generatedSignatures?.signatures)
    ? generatedSignatures.signatures
    : [];
  const generatedSignatureCount = generatedSignatureList.length;
  const hasGeneratedSignatures = generatedSignatureCount > 0;

  const selectedSignatureIndexSet = useMemo(
    () => new Set(selectedSignatureIndices),
    [selectedSignatureIndices],
  );

  const selectedVoiceSignatures = useMemo(() => {
    if (!hasGeneratedSignatures || selectedSignatureIndexSet.size === 0) {
      return [];
    }
    return generatedSignatureList.filter((_, index) =>
      selectedSignatureIndexSet.has(index),
    );
  }, [
    generatedSignatureList,
    hasGeneratedSignatures,
    selectedSignatureIndexSet,
  ]);

  const hasSelectedVoiceSignatures = selectedVoiceSignatures.length > 0;

  const addResult = useCallback(
    (testName, success, data = null, error = null, duration = null) => {
      setTestResults((prev) => {
        const entry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          testName,
          success,
          data,
          error,
          duration,
          timestamp: new Date().toLocaleTimeString(),
        };
        return [entry, ...prev].slice(0, 200);
      });
    },
    [],
  );

  const authenticatedFetch = useCallback(
    (url, options = {}) => {
      const headers = new Headers(options.headers || {});
      if (apiToken) {
        headers.set("Authorization", `Bearer ${apiToken}`);
      }
      if (!headers.has("Accept")) {
        headers.set("Accept", "application/json");
      }
      return fetch(url, {
        ...options,
        headers,
      });
    },
    [apiToken],
  );

  // In development, auth goes to :3001 and AI/transcription goes to :8000
  // In production, both go through nginx proxy on :8080
  const getAPIUrl = () => {
    return (
      import.meta.env.VITE_TRANSCRIPTION_API_URL || "http://localhost:8000"
    );
  };

  // Auth endpoints (like token validation) should always go to auth backend
  const getAuthAPIUrl = () => {
    return import.meta.env.VITE_API_URL || "http://localhost:3030/api";
  };

  const API_BASE_URL = getAPIUrl();
  const AUTH_API_BASE_URL = getAuthAPIUrl();
  const WS_BASE_URL = API_BASE_URL?.replace("http", "ws");

  const modelDictionary = useMemo(() => {
    const lookup = {};
    availableModels.forEach((model) => {
      if (model?.id) {
        lookup[model.id] = model;
      }
    });
    return lookup;
  }, [availableModels]);

  const getModelMeta = useCallback(
    (modelId) =>
      modelDictionary[modelId] || {
        id: modelId,
        label: modelId || "Modele inconnu",
        description: "",
        available: false,
      },
    [modelDictionary],
  );

  const selectedModelMeta = getModelMeta(selectedModelId);
  const glassPanelClass =
    "rounded-2xl border border-white/10 bg-white/5 backdrop-blur-2xl shadow-[0_25px_80px_rgba(2,6,23,0.55)] p-6";
  const glassInsetClass =
    "rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl";
  const glassHighlightClass =
    "rounded-2xl border border-indigo-300/50 bg-gradient-to-br from-white/25 via-white/10 to-indigo-500/10 backdrop-blur-3xl shadow-[0_35px_120px_rgba(76,29,149,0.35)]";
  const glassMetricClass =
    "rounded-2xl border border-white/15 bg-gradient-to-br from-indigo-600/20 via-slate-950/40 to-transparent backdrop-blur-3xl shadow-[0_35px_110px_rgba(15,23,42,0.65)]";
  const glassInputClass =
    "w-full rounded-xl border border-white/20 bg-slate-950/40 px-3 py-2 text-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-indigo-400/70 focus:border-indigo-300/70";

  const fetchModels = useCallback(
    async (opts = {}) => {
      setModelsLoading(true);
      if (!opts.silent) {
        setModelFetchError(null);
      }

      try {
        const endpoint = `${API_BASE_URL}/models`;
        const response = await authenticatedFetch(endpoint);

        if (!response.ok) {
          const errorPayload = await response
            .json()
            .catch(() => ({ detail: response.statusText }));
          throw new Error(
            errorPayload.detail ||
              `Impossible de charger les modeles (${response.status})`,
          );
        }

        const payload = await response.json();
        const details = Array.isArray(payload?.details) ? payload.details : [];
        const normalized = [];
        const seen = new Set();

        const source = details.length ? details : FALLBACK_MODELS;
        source.forEach((detail) => {
          if (!detail || !detail.id || seen.has(detail.id)) {
            return;
          }
          seen.add(detail.id);
          normalized.push({
            id: detail.id,
            label: detail.label || detail.id,
            description:
              detail.description ||
              detail.notes ||
              "Aucune description disponible.",
            backend: detail.backend,
            release: detail.release,
            dataset: detail.dataset,
            quantization: detail.quantization,
            notes: detail.notes,
            available: detail.available !== false,
            live_capable: detail.live_capable !== false,
          });
        });

        if (!normalized.length) {
          normalized.push(...FALLBACK_MODELS);
        }

        normalized.sort((a, b) => {
          if (a.id === DEFAULT_MODEL_ID) return -1;
          if (b.id === DEFAULT_MODEL_ID) return 1;
          return a.label.localeCompare(b.label);
        });

        setAvailableModels(normalized);

        const defaultFromServer = payload?.default || DEFAULT_MODEL_ID;
        setSelectedModelId((currentId) => {
          const hasCurrentSelection = normalized.some(
            (model) => model.id === currentId && model.available,
          );
          if (hasCurrentSelection) {
            return currentId;
          }
          const fallback =
            normalized.find(
              (model) => model.id === defaultFromServer && model.available,
            ) ||
            normalized.find((model) => model.available) ||
            normalized[0];
          return fallback ? fallback.id : currentId || DEFAULT_MODEL_ID;
        });
      } catch (error) {
        console.warn("Model fetch failed:", error);
        setModelFetchError(
          error.message || "Impossible de charger les modeles",
        );
        setAvailableModels(FALLBACK_MODELS);
      } finally {
        setModelsLoading(false);
      }
    },
    [API_BASE_URL, authenticatedFetch],
  );

  // Auto-load API token from localStorage on mount (if previously saved)
  // Note: This loads API tokens, not JWT tokens. Use "Use JWT Token" button for JWTs.
  useEffect(() => {
    const storedApiToken = localStorage.getItem("saved_api_token");
    if (storedApiToken && !apiToken) {
      setApiToken(storedApiToken);
    }
  }, []); // Empty deps - only run once on mount

  useEffect(() => {
    fetchModels({ silent: true });
  }, [fetchModels]);

  useEffect(() => {
    if (!hasGeneratedSignatures) {
      setSelectedSignatureIndices([]);
      return;
    }

    setSelectedSignatureIndices((prev) => {
      if (!prev.length) {
        return Array.from(
          { length: generatedSignatureCount },
          (_, index) => index,
        );
      }
      const validSelections = prev.filter(
        (index) => index >= 0 && index < generatedSignatureCount,
      );
      return validSelections.length
        ? validSelections
        : Array.from({ length: generatedSignatureCount }, (_, index) => index);
    });
  }, [hasGeneratedSignatures, generatedSignatureCount]);

  const toggleSignatureSelection = (index) => {
    setSelectedSignatureIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((value) => value !== index);
      }
      return [...prev, index].sort((a, b) => a - b);
    });
  };

  const selectAllSignatures = () => {
    if (!hasGeneratedSignatures) return;
    setSelectedSignatureIndices(
      Array.from({ length: generatedSignatureCount }, (_, index) => index),
    );
  };

  const clearSignatureSelection = () => {
    setSelectedSignatureIndices([]);
  };

  // Handle signature audio file selection
  const handleSignatureFilesChange = (files) => {
    setSignatureAudioFiles(files);

    // Initialize speaker names for new files
    const newNames = { ...signatureSpeakerNames };
    files.forEach((file, index) => {
      if (!(index in newNames)) {
        // Extract name from filename (remove extension)
        const defaultName = file.name.replace(/\.[^/.]+$/, "");
        newNames[index] = defaultName;
      }
    });

    // Remove entries for files that no longer exist
    Object.keys(newNames).forEach((key) => {
      const index = parseInt(key);
      if (index >= files.length) {
        delete newNames[index];
      }
    });

    setSignatureSpeakerNames(newNames);
  };

  // Update signature speaker name
  const updateSignatureSpeakerName = (index, name) => {
    setSignatureSpeakerNames((prev) => ({
      ...prev,
      [index]: name,
    }));
  };

  // Generate voice signatures
  const generateVoiceSignatures = async () => {
    if (!apiToken) {
      addResult(
        "Signature Generation",
        false,
        null,
        "Veuillez saisir un token API",
      );
      return;
    }

    if (signatureAudioFiles.length === 0) {
      addResult(
        "Signature Generation",
        false,
        null,
        "Veuillez selectionner des fichiers audio",
      );
      return;
    }

    setGeneratingSignatures(true);
    setGeneratedSignatures(null);
    setSelectedSignatureIndices([]);
    const startTime = Date.now();

    try {
      const formData = new FormData();

      // Add audio files
      signatureAudioFiles.forEach((file) => {
        formData.append("audio_files", file);
      });

      // Add speaker names as JSON array
      const names = signatureAudioFiles.map(
        (_, index) => signatureSpeakerNames[index] || `Speaker ${index + 1}`,
      );
      formData.append("speaker_names", JSON.stringify(names));

      const API_BASE_URL = getAPIUrl();
      const response = await authenticatedFetch(
        `${API_BASE_URL}/generate_signatures`,
        {
          method: "POST",
          body: formData,
        },
      );

      const duration = Date.now() - startTime;

      if (response.ok) {
        const data = await response.json();
        setGeneratedSignatures(data);
        setSelectedSignatureIndices(
          Array.from(
            { length: data?.signatures?.length || 0 },
            (_, index) => index,
          ),
        );

        addResult(
          "Signature Generation",
          true,
          {
            count: data.count,
            signatures: data.signatures.map((s) => ({
              name: s.name,
              embedding_dims: s.embedding.length,
            })),
            size_estimate: `~${Math.round(
              JSON.stringify(data.signatures).length / 1024,
            )} KB`,
          },
          null,
          `${duration}ms`,
        );
      } else {
        const errorData = await response.json();
        addResult(
          "Signature Generation",
          false,
          null,
          errorData.detail || response.statusText,
          `${duration}ms`,
        );
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error("❌ Signature generation error:", error);
      addResult(
        "Signature Generation",
        false,
        null,
        error.message,
        `${duration}ms`,
      );
    } finally {
      setGeneratingSignatures(false);
    }
  };

  // Copy signatures to clipboard
  const copySignaturesToClipboard = () => {
    if (!generatedSignatures) return;

    const jsonString = JSON.stringify(generatedSignatures.signatures, null, 2);
    navigator.clipboard
      .writeText(jsonString)
      .then(() => {
        addResult(
          "Copy to Clipboard",
          true,
          { action: "Signatures copiees !" },
          null,
        );
      })
      .catch((error) => {
        addResult("Copy to Clipboard", false, null, error.message);
      });
  };

  // Save signatures to localStorage
  const saveSignaturesToStorage = () => {
    if (!generatedSignatures) return;

    try {
      localStorage.setItem(
        "voice_signatures",
        JSON.stringify(generatedSignatures.signatures),
      );
      localStorage.setItem(
        "voice_signatures_metadata",
        JSON.stringify({
          generated_at: new Date().toISOString(),
          count: generatedSignatures.count,
        }),
      );
      addResult(
        "Save to Storage",
        true,
        { action: "Sauvegarde dans localStorage" },
        null,
      );
    } catch (error) {
      addResult("Save to Storage", false, null, error.message);
    }
  };

  const loadSignaturesFromStorage = () => {
    try {
      const stored = localStorage.getItem("voice_signatures");
      if (!stored) {
        throw new Error("Aucune signature sauvegardee dans localStorage");
      }

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        throw new Error("Les signatures sauvegardees sont vides ou invalides");
      }

      const metadata = (() => {
        try {
          return (
            JSON.parse(
              localStorage.getItem("voice_signatures_metadata") || "{}",
            ) || {}
          );
        } catch {
          return {};
        }
      })();

      setGeneratedSignatures({
        count: parsed.length,
        signatures: parsed,
        metadata,
        source: "localStorage",
      });
      setSelectedSignatureIndices(
        Array.from({ length: parsed.length }, (_, index) => index),
      );

      addResult(
        "Load from Storage",
        true,
        { count: parsed.length, generated_at: metadata.generated_at },
        null,
      );
    } catch (error) {
      addResult("Load from Storage", false, null, error.message);
    }
  };

  // Test API Token Validity
  const testApiToken = async () => {
    if (!apiToken) {
      addResult("Test token", false, null, "Veuillez saisir un token API");
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      const response = await authenticatedFetch(
        `${AUTH_API_BASE_URL}/tokens/validate`,
      );
      const duration = Date.now() - startTime;

      if (response.ok) {
        const data = await response.json();
        addResult("Test token", true, data, null, `${duration}ms`);
      } else {
        const errorData = await response.json();
        addResult(
          "Test token",
          false,
          errorData,
          `HTTP ${response.status}`,
          `${duration}ms`,
        );
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      addResult("Test token", false, null, error.message, `${duration}ms`);
    }

    setLoading(false);
  };

  // Live WebSocket Transcription
  const startLiveTranscription = async () => {
    if (!apiToken) {
      addResult(
        "Transcription live",
        false,
        null,
        "Veuillez saisir un token API",
      );
      return;
    }

    try {
      let ws = wsConnection;
      let needsNewConnection = true;
      let stream = null;

      if (ws) {
        if (ws.readyState === WebSocket.OPEN) {
          needsNewConnection = false;
        } else if (ws.readyState === WebSocket.CONNECTING) {
          try {
            await new Promise((resolve, reject) => {
              const timeout = setTimeout(
                () => reject(new Error("Connection timeout")),
                5000,
              );
              ws.addEventListener(
                "open",
                () => {
                  clearTimeout(timeout);
                  resolve();
                },
                { once: true },
              );
              ws.addEventListener(
                "error",
                () => {
                  clearTimeout(timeout);
                  reject(new Error("Connection failed"));
                },
                { once: true },
              );
              ws.addEventListener(
                "close",
                () => {
                  clearTimeout(timeout);
                  reject(new Error("Connection closed"));
                },
                { once: true },
              );
            });
            needsNewConnection = false;
          } catch (err) {
            console.error("Failed to wait for connection:", err.message);
            needsNewConnection = true;
          }
        } else {
          needsNewConnection = true;
        }
      }

      if (needsNewConnection) {
        // Build WebSocket URL with parameters (excluding voice signatures to avoid URL length limits)
        const params = new URLSearchParams({
          token: apiToken,
        });

        // Add diarization parameter
        if (liveDiarization !== undefined) {
          params.append("enable_diarization", liveDiarization.toString());
        }

        if (liveDiarization) {
          params.append(
            "voice_similarity_threshold",
            liveVoiceSimilarityThreshold,
          );
          params.append(
            "anonymous_similarity_threshold",
            liveAnonymousSimilarityThreshold,
          );
        }

        if (selectedModelId) {
          params.append("model", selectedModelId);
        }

        // Note: Voice signatures will be sent as first message after connection
        const hasSignatures = liveUseSignatures && hasSelectedVoiceSignatures;
        const liveVoiceSignatures = hasSignatures
          ? selectedVoiceSignatures
          : [];

        if (hasSignatures) {
          addResult(
            "Voice Signatures",
            true,
            `${selectedVoiceSignatures.length} signatures vocales seront envoyees apres la connexion`,
            null,
          );
        }

        const wsUrl = `${WS_BASE_URL}/asr?${params.toString()}`;

        addResult(
          "WebSocket Attempt",
          true,
          {
            url: wsUrl.replace(apiToken, "***TOKEN***"), // Hide token in logs
            model: selectedModelId,
            diarization: liveDiarization,
            signatures: hasSignatures ? selectedVoiceSignatures.length : 0,
          },
          null,
          null,
        );

        // Create new WebSocket and set up handlers BEFORE waiting
        ws = new WebSocket(wsUrl);

        // Set up permanent handlers first
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // Handle config acknowledgment
            if (data.type === "config_ack") {
              return;
            }

            // Handle the new server format with lines array
            if (data.status === "active_transcription" && data.lines) {
              // Filter and merge consecutive same-speaker lines
              const filteredLines = data.lines.filter(
                (line) => line.text && line.text.trim() !== "",
              );

              // Merge consecutive lines from the same speaker
              const mergedLines = [];
              filteredLines.forEach((line) => {
                const speakerLabel =
                  line.speaker_name || `Speaker ${line.speaker}`;

                // Check if we can merge with the last line
                if (
                  mergedLines.length > 0 &&
                  mergedLines[mergedLines.length - 1].speaker === speakerLabel
                ) {
                  // Merge: append text to the last line
                  mergedLines[mergedLines.length - 1].text +=
                    " " + line.text.trim();
                } else {
                  // New speaker: add new line
                  mergedLines.push({
                    speaker: speakerLabel,
                    text: line.text.trim(),
                  });
                }
              });

              // Convert merged lines to transcript text
              const transcriptText = mergedLines
                .map((line) => `${line.speaker}: ${line.text}`)
                .join("\n");

              if (transcriptText) {
                setLiveTranscript(transcriptText);
              }
            }

            // Legacy format support (if server still sends this)
            if (data.transcript) {
              setLiveTranscript((prev) => prev + " " + data.transcript);
            }

            if (data.final) {
              addResult("Live Transcript Segment", true, data, null, null);
            }
          } catch (error) {
            console.error("WebSocket message error:", error);
            addResult("WebSocket Message Error", false, null, error.message);
          }
        };

        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
          addResult(
            "Live Transcription",
            false,
            null,
            "Erreur WebSocket: " + (error.message || "Connexion echouee"),
          );
          setIsRecording(false);
        };

        ws.onclose = (event) => {
          const isAuthError = event.code === 1008;
          addResult(
            "Transcription live",
            !isAuthError,
            isAuthError ? null : "WebSocket fermé",
            isAuthError
              ? `Token invalide ou révoqué — accès refusé (code ${event.code})`
              : null,
            null,
          );
          setIsRecording(false);
          setWsConnection(null);
        };

        // Save to state immediately
        setWsConnection(ws);

        // Now wait for connection to open
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("WebSocket connection timeout"));
          }, 10000);

          ws.addEventListener(
            "open",
            () => {
              clearTimeout(timeout);
              addResult(
                "Live Transcription",
                true,
                "WebSocket connecte avec succes",
                null,
                null,
              );

              // Send voice signatures as first message if diarization is enabled
              if (liveDiarization) {
                const configMessage = {
                  type: "config",
                  voice_signatures: liveVoiceSignatures,
                };
                ws.send(JSON.stringify(configMessage));

                // Wait for config acknowledgment before proceeding
                const configAckTimeout = setTimeout(() => {
                  resolve();
                }, 3000);

                const handleConfigAck = (event) => {
                  try {
                    const msg = JSON.parse(event.data);
                    if (msg.type === "config_ack") {
                      clearTimeout(configAckTimeout);
                      ws.removeEventListener("message", handleConfigAck);
                      resolve();
                    }
                  } catch (e) {
                    // Not a JSON message, ignore
                  }
                };

                ws.addEventListener("message", handleConfigAck);
              } else {
                resolve();
              }
            },
            { once: true },
          );

          ws.addEventListener(
            "error",
            (err) => {
              clearTimeout(timeout);
              reject(new Error("WebSocket connection failed"));
            },
            { once: true },
          );

          ws.addEventListener(
            "close",
            () => {
              clearTimeout(timeout);
              reject(new Error("WebSocket closed before opening"));
            },
            { once: true },
          );
        });
      }

      // **STEP 2: Get microphone access only when ready to use it**
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Guard: verify the WebSocket is still open after the async getUserMedia call.
      // The server may have closed it (e.g. code 1008 for auth failure) while we
      // were waiting, which would cause setIsRecording(true) below to override the
      // setIsRecording(false) already called by onclose.
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        throw new Error(
          ws?.readyState === WebSocket.CLOSING ||
            ws?.readyState === WebSocket.CLOSED
            ? "La connexion WebSocket a été fermée par le serveur (token invalide ou révoqué)"
            : "WebSocket non disponible",
        );
      }

      // **STEP 3: Set up audio recording**
      const audioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
          ws.send(event.data);
        }
      };

      mediaRecorder.start(250); // Send data every 250ms
      mediaRecorderRef.current = mediaRecorder;

      setIsRecording(true);
      setLiveTranscript("");
    } catch (error) {
      addResult("Live Transcription", false, null, error.message);

      // Clean up any resources if something went wrong
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream
            .getTracks()
            .forEach((track) => track.stop());
        }
        mediaRecorderRef.current = null;
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    }
  };

  const stopLiveTranscription = () => {
    // Stop MediaRecorder and close audio stream
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      mediaRecorderRef.current = null;
    }

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    // Send end-of-session message to backend for graceful cleanup
    if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
      try {
        wsConnection.send(JSON.stringify({ type: "end_session" }));
      } catch (error) {
        console.error("Failed to send end_session message:", error);
      }
      // Close WebSocket connection gracefully
      wsConnection.close(1000, "Session ended by user");
      setWsConnection(null);
    }

    setIsRecording(false);
    audioChunksRef.current = [];
  };

  // File Upload Transcription
  const uploadAudioFile = async () => {
    if (!apiToken) {
      addResult("Import fichier", false, null, "Veuillez saisir un token API");
      return;
    }

    if (!selectedFile) {
      addResult(
        "Import fichier",
        false,
        null,
        "Veuillez selectionner un fichier audio",
      );
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      const formData = new FormData();
      formData.append("audio", selectedFile);

      const endpoint = `${API_BASE_URL}/transcribe/sync`;

      if (fileUseSignatures) {
        formData.append("diarization", "true");
        formData.append(
          "anonymous_similarity_threshold",
          fileAnonymousSimilarityThreshold,
        );
        if (hasSelectedVoiceSignatures) {
          formData.append(
            "voice_signatures",
            JSON.stringify(selectedVoiceSignatures),
          );
          formData.append(
            "voice_similarity_threshold",
            fileVoiceSimilarityThreshold,
          );
        }
      }

      const response = await authenticatedFetch(endpoint, {
        method: "POST",
        body: formData,
      });

      const duration = Date.now() - startTime;

      if (response.ok) {
        const data = await response.json();
        setUploadResults(data);
        addResult(
          "Import fichier",
          true,
          {
            ...data,
            used_model: selectedModelId,
          },
          null,
          `${duration}ms`,
        );
      } else {
        const errorData = await response.json();
        addResult(
          "Import fichier",
          false,
          errorData,
          `HTTP ${response.status}`,
          `${duration}ms`,
        );
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      addResult("Import fichier", false, null, error.message, `${duration}ms`);
    }

    setLoading(false);
  };

  // Async File Upload (Background Processing)
  const submitAsyncTranscription = async () => {
    if (!apiToken) {
      addResult(
        "Soumission async",
        false,
        null,
        "Veuillez saisir un token API",
      );
      return;
    }

    if (!selectedFile) {
      addResult(
        "Soumission async",
        false,
        null,
        "Veuillez selectionner un fichier audio",
      );
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      const formData = new FormData();
      formData.append("audio", selectedFile);

      if (fileUseSignatures) {
        formData.append("diarization", "true");
        formData.append(
          "anonymous_similarity_threshold",
          fileAnonymousSimilarityThreshold,
        );
        if (hasSelectedVoiceSignatures) {
          formData.append(
            "voice_signatures",
            JSON.stringify(selectedVoiceSignatures),
          );
          formData.append(
            "voice_similarity_threshold",
            fileVoiceSimilarityThreshold,
          );
        }
      }

      const response = await authenticatedFetch(
        `${API_BASE_URL}/transcribe/submit`,
        {
          method: "POST",
          body: formData,
        },
      );

      const duration = Date.now() - startTime;

      if (response.ok) {
        const data = await response.json();
        addResult(
          "Soumission async",
          true,
          {
            ...data,
            used_model: selectedModelId,
          },
          null,
          `${duration}ms`,
        );

        // Start polling for results
        if (data.task_id) {
          pollTaskStatus(data.task_id);
        } else {
          console.error("[SUBMIT] No task_id in response!");
        }
      } else {
        const errorData = await response.json();
        addResult(
          "Soumission async",
          false,
          errorData,
          `HTTP ${response.status}`,
          `${duration}ms`,
        );
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      addResult(
        "Soumission async",
        false,
        null,
        error.message,
        `${duration}ms`,
      );
    }

    setLoading(false);
  };

  // Poll task status
  const pollTaskStatus = async (taskId) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes with 5 second intervals

    const poll = async () => {
      try {
        const statusResponse = await authenticatedFetch(
          `${API_BASE_URL}/transcribe/status/${taskId}`,
        );
        const statusData = await statusResponse.json();

        if (statusResponse.ok) {
          const shortId = taskId.substring(0, 8);
          const statusPayload = {
            status: statusData.status,
            progress: statusData.progress,
            current_step: statusData.current_step,
            model_id: statusData.model_id || selectedModelId,
          };
          addResult(
            `Task ${shortId}... Status: ${statusData.status}`,
            true,
            statusPayload,
          );

          if (
            statusData.status === "completed" ||
            statusData.status === "succeeded"
          ) {
            try {
              const resultResponse = await authenticatedFetch(
                `${API_BASE_URL}/transcribe/result/${taskId}`,
              );

              if (!resultResponse.ok) {
                const errorData = await resultResponse.json();
                addResult(
                  `Task ${shortId}... Result Fetch Failed`,
                  false,
                  errorData,
                  `HTTP ${resultResponse.status}`,
                );
                return;
              }

              const resultData = await resultResponse.json();
              setUploadResults(resultData);
              addResult(`Task ${shortId}... Completed ✅`, true, {
                text_preview: resultData.text?.substring(0, 100) + "...",
                text_length: resultData.text?.length || 0,
                segments_count: resultData.segments?.length || 0,
                duration: resultData.duration,
                language: resultData.language,
                unique_speakers:
                  resultData.diarization_summary?.unique_speakers,
                tokens_consumed: resultData.token_usage?.tokens_consumed,
                model_id:
                  resultData.model_id || statusData.model_id || selectedModelId,
                signature_refinement: resultData.signature_refinement
                  ? {
                      mode: resultData.signature_refinement.mode,
                      applied: resultData.signature_refinement.applied,
                    }
                  : undefined,
              });
            } catch (error) {
              console.error("[POLL] Error fetching result:", error);
              addResult(
                `Task ${shortId}... Result Error`,
                false,
                null,
                error.message,
              );
            }
            return;
          }

          if (statusData.status === "failed") {
            addResult(
              `Task ${shortId}... Failed ❌`,
              false,
              statusData,
              statusData.error,
            );
            return;
          }

          if (statusData.status === "cancelled") {
            addResult(
              `Task ${shortId}... Cancelled`,
              false,
              statusData,
              "Task was cancelled",
            );
            return;
          }
        } else {
          console.error(
            "[POLL] Status check failed:",
            statusResponse.status,
            statusResponse.statusText,
          );
          addResult(
            `Task ${taskId.substring(0, 8)}... Status Check Failed`,
            false,
            statusResponse,
            `HTTP ${statusResponse.status}`,
          );
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          addResult(
            `Task ${taskId} Timeout`,
            false,
            null,
            "Max polling attempts reached",
          );
        }
      } catch (error) {
        console.error("[POLL] Poll error:", error);
        addResult(`Task ${taskId} Poll Error`, false, null, error.message);
      }
    };

    poll();
  };

  const clearResults = () => {
    setTestResults([]);
    setUploadResults(null);
    setLiveTranscript("");
  };

  return (
    <div className="space-y-6 text-white/95">
      {/* API Token Input */}
      <div className={glassPanelClass}>
        <h3 className="text-lg font-semibold mb-4">
          🔑 Configuration du token API
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Token API (format epv_xxxxxxxx_xxxxxxxx)
            </label>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  type={showToken ? "text" : "password"}
                  name="api-token"
                  autoComplete="new-password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder="Entrez votre token API (epv_xxxxxxxx_xxxxxxxx)..."
                  className={`${glassInputClass} pr-10 font-mono`}
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  tabIndex={-1}
                >
                  {showToken ? "🙈" : "👁️"}
                </button>
              </div>
              <button
                onClick={testApiToken}
                disabled={loading || !apiToken}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                🧪 Tester le token
              </button>
              <button
                onClick={() => {
                  const token = localStorage.getItem("token");
                  if (token) {
                    setApiToken(token);
                  } else {
                    alert("Aucun token JWT trouve. Veuillez vous connecter.");
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                🔐 Utiliser le token JWT
              </button>
              <button
                onClick={() => {
                  if (apiToken) {
                    localStorage.setItem("saved_api_token", apiToken);
                    addResult(
                      "Save Token",
                      true,
                      { action: "Token enregistre pour la prochaine session" },
                      null,
                    );
                  }
                }}
                disabled={!apiToken}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                title="Sauvegarder ce token API pour le recharger automatiquement"
              >
                💾 Memoriser le token
              </button>
            </div>
          </div>

          {user && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              💡 Creez des tokens API dans la page "API Tokens" en etant
              connecte en tant que {user.email}. Utilisez des tokens API dedies
              (pas le JWT) en production. Cliquez sur "Memoriser le token" pour
              le recharger automatiquement.
            </div>
          )}
        </div>
      </div>

      {/* Model Selection */}
      <div className={glassPanelClass}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-3">
              🎤 Modele — Transcription live
              <span className="text-xs font-normal px-2 py-0.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 tracking-wide">
                Live WebSocket uniquement
              </span>
            </h3>
            <p className="text-sm text-white/50 mt-1">
              Ce modele est utilise exclusivement pour la transcription live en
              temps reel. La transcription de fichiers (sync/async) utilise le
              modele configure cote serveur.
            </p>
          </div>
          <button
            onClick={() => fetchModels({ silent: false })}
            disabled={modelsLoading}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {modelsLoading ? "Actualisation..." : "Actualiser les modeles"}
          </button>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-2">
              Modele actif
            </label>
            <select
              value={selectedModelId}
              onChange={(e) => setSelectedModelId(e.target.value)}
              className={glassInputClass}
            >
              {availableModels
                .filter((m) => m.live_capable)
                .map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.label}
                    {!model.available ? " (indisponible)" : ""}
                  </option>
                ))}
            </select>
            {modelFetchError && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                {modelFetchError}
              </p>
            )}
          </div>

          <div className={`${glassHighlightClass} p-4`}>
            <p className="text-xs uppercase tracking-wide text-indigo-100/80">
              Metadonnees
            </p>
            <h4 className="text-lg font-semibold text-white mt-1">
              {selectedModelMeta.label}
            </h4>
            <p className="text-sm text-white/80 mt-2">
              {selectedModelMeta.description ||
                "Aucune description disponible."}
            </p>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-white/70">
              {selectedModelMeta.backend && (
                <div>
                  <dt className="uppercase tracking-wide">Backend</dt>
                  <dd className="text-white">{selectedModelMeta.backend}</dd>
                </div>
              )}
              {selectedModelMeta.release && (
                <div>
                  <dt className="uppercase tracking-wide">Release</dt>
                  <dd className="text-white">{selectedModelMeta.release}</dd>
                </div>
              )}
              {selectedModelMeta.quantization && (
                <div>
                  <dt className="uppercase tracking-wide">Quantization</dt>
                  <dd className="text-white">
                    {selectedModelMeta.quantization}
                  </dd>
                </div>
              )}
              <div>
                <dt className="uppercase tracking-wide">Status</dt>
                <dd
                  className={`text-sm font-medium ${
                    selectedModelMeta.available
                      ? "text-emerald-300"
                      : "text-rose-300"
                  }`}
                >
                  {selectedModelMeta.available ? "Pret" : "Indisponible"}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <p className="mt-4 text-xs text-white/40 border-t border-white/10 pt-3">
          Ce choix s'applique uniquement au WebSocket live ci-dessous. Les
          imports de fichiers ignorent ce parametre.
        </p>
      </div>

      {/* Voice Signature Generation */}
      <div className={glassPanelClass}>
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          🎭 Generation de signatures vocales
          <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
            Confidentialite d'abord
          </span>
        </h3>

        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔒</span>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-semibold mb-1">Aucun stockage serveur</p>
                <p>
                  Les fichiers audio sont traites immediatement et les
                  signatures vous sont renvoyees. Rien n'est stocke sur le
                  serveur.
                </p>
              </div>
            </div>
          </div>

          {/* File Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Selectionner des echantillons audio par speaker (1-50 fichiers)
            </label>
            <input
              type="file"
              accept="audio/*"
              multiple
              onChange={(e) =>
                handleSignatureFilesChange(Array.from(e.target.files))
              }
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 dark:file:bg-purple-900 dark:file:text-purple-200"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Importez des echantillons audio (WAV, MP3, M4A, FLAC, OGG) - un
              fichier par speaker. Chaque fichier doit contenir 3 a 10 secondes
              de parole claire.
            </div>
          </div>

          {/* Speaker Names Input */}
          {signatureAudioFiles.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Noms des speakers
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {signatureAudioFiles.map((file, index) => (
                  <div
                    key={index}
                    className={`${glassInsetClass} flex items-center space-x-3 p-3`}
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-700 dark:text-purple-300 font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">
                        📁 {file.name}
                      </div>
                      <input
                        type="text"
                        value={signatureSpeakerNames[index] || ""}
                        onChange={(e) =>
                          updateSignatureSpeakerName(index, e.target.value)
                        }
                        placeholder={`Nom du speaker ${index + 1}`}
                        className={glassInputClass}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                💡 Les noms de speakers sont extraits automatiquement des noms
                de fichier mais peuvent etre personnalises
              </div>
            </div>
          )}

          {/* Generate Button */}
          <div className="flex gap-3">
            <button
              onClick={generateVoiceSignatures}
              disabled={
                generatingSignatures ||
                !apiToken ||
                signatureAudioFiles.length === 0
              }
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center"
            >
              {generatingSignatures ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generation...
                </>
              ) : (
                <>⚡ Generer les signatures</>
              )}
            </button>

            {signatureAudioFiles.length > 0 && (
              <button
                onClick={() => {
                  setSignatureAudioFiles([]);
                  setSignatureSpeakerNames({});
                  setGeneratedSignatures(null);
                  setSelectedSignatureIndices([]);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                🗑️ Vider
              </button>
            )}

            <button
              type="button"
              onClick={loadSignaturesFromStorage}
              className="px-4 py-2 bg-white/10 text-white rounded border border-white/20 hover:bg-white/20"
            >
              📦 Charger un lot sauvegarde
            </button>
          </div>

          {signatureAudioFiles.length > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Selectionne: {signatureAudioFiles.length} fichier(s) · Taille
              estimee: ~{Math.round(signatureAudioFiles.length * 0.1)} KB
            </div>
          )}
        </div>

        {/* Generated Signatures Display */}
        {generatedSignatures && (
          <div className="mt-6 space-y-4">
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-green-700 dark:text-green-300 flex items-center">
                  ✅ Signatures generees
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={copySignaturesToClipboard}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 flex items-center"
                  >
                    📋 Copier le JSON
                  </button>
                  <button
                    onClick={saveSignaturesToStorage}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 flex items-center"
                  >
                    💾 Sauvegarder
                  </button>
                </div>
              </div>

              <div className={`${glassMetricClass} p-4`}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-white/70">
                      Nombre
                    </div>
                    <div className="text-2xl font-semibold text-white">
                      {generatedSignatures.count} speakers
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-white/70">
                      Taille totale
                    </div>
                    <div className="text-2xl font-semibold text-white">
                      ~
                      {Math.round(
                        JSON.stringify(generatedSignatures.signatures).length /
                          1024,
                      )}{" "}
                      KB
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-white/80">
                    Signatures:
                  </div>
                  {generatedSignatures.signatures.map((sig, index) => {
                    // Support both old (vector) and new (embedding) formats
                    const features = sig.embedding || sig.vector;
                    const featureType = sig.embedding
                      ? sig.encoder_type || "ecapa-tdnn"
                      : "MFCC (legacy)";

                    return (
                      <div
                        key={index}
                        className={`${glassInsetClass} flex items-center justify-between p-3 text-white`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-indigo-500/30 text-white font-semibold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium text-white">
                              {sig.name}
                            </div>
                            <div className="text-xs text-white/70">
                              {features
                                ? `${featureType} • ${features.length} dims`
                                : "Aucune donnee"}
                            </div>
                          </div>
                        </div>
                        {sig.embedding_hash || sig.vector_hash ? (
                          <span className="text-xs text-white/70 font-mono">
                            {sig.embedding_hash || sig.vector_hash}
                          </span>
                        ) : (
                          <span className="text-xs text-white/50">
                            Aucun hash
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-amber-200/30 bg-gradient-to-br from-amber-500/20 via-transparent to-amber-300/5 backdrop-blur-2xl p-4 text-amber-100 shadow-[0_30px_80px_rgba(146,64,14,0.35)]">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div className="text-sm">
                    <p className="font-semibold mb-1 tracking-wide uppercase text-amber-200/90">
                      Etapes suivantes
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-white/80">
                      <li>
                        Copiez le JSON ou sauvegardez-le dans localStorage
                      </li>
                      <li>
                        Utilisez ces signatures avec la transcription live
                        WebSocket
                      </li>
                      <li>
                        Passez-les via le parametre{" "}
                        <code className="px-1 rounded bg-white/10 text-white">
                          voice_signatures
                        </code>
                      </li>
                      <li>
                        Ou importez un fichier avec signatures pour la
                        transcription async
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Live Transcription */}
      <div className={glassPanelClass}>
        <h3 className="text-lg font-semibold mb-4">🎤 Transcription live</h3>

        <div className="space-y-4">
          {/* Live Transcription Options */}
          {!isRecording && (
            <div className={`${glassInsetClass} p-4 space-y-3`}>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                ⚙️ Options
              </h4>

              <div className={`${glassInsetClass} p-3`}>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Modele Whisper
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Utilisation de {selectedModelMeta.label}.{" "}
                  {selectedModelMeta.description || DEFAULT_MODEL_DESCRIPTION}
                </p>
              </div>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={liveDiarization}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setLiveDiarization(checked);
                    // Automatically enable signatures when diarization is enabled (required)
                    if (checked) {
                      setLiveUseSignatures(true);
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Activer la diarisation des speakers
                    {hasGeneratedSignatures && (
                      <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                        {selectedVoiceSignatures.length} speaker
                        {selectedVoiceSignatures.length === 1 ? "" : "s"}{" "}
                        selectionne(s)
                      </span>
                    )}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {hasSelectedVoiceSignatures ? (
                      selectedVoiceSignatures.some(
                        (s) => s.vector && !s.embedding,
                      ) ? (
                        <span className="text-orange-600 dark:text-orange-400 font-medium">
                          ⚠️ Anciennes signatures MFCC detectees - veuillez
                          regenerer avec les embeddings ECAPA-TDNN
                        </span>
                      ) : (
                        `Identification des speakers: ${selectedVoiceSignatures
                          .map((s) => s.name)
                          .join(", ")}`
                      )
                    ) : liveDiarization ? (
                      "Mode anonyme : les locuteurs seront etiquetes Speaker 0, Speaker 1, ... (aucune signature selectionnee)"
                    ) : (
                      "Fonctionne avec ou sans signatures vocales (mode anonyme si aucune n'est selectionnee)"
                    )}
                  </p>
                </div>
              </label>

              {liveDiarization && (
                <div className="grid grid-cols-2 gap-3 pl-7">
                  <label className="block">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Seuil signatures connues
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={liveVoiceSimilarityThreshold}
                      onChange={(e) =>
                        setLiveVoiceSimilarityThreshold(e.target.value)
                      }
                      className="mt-1 w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Seuil regroupement anonyme
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={liveAnonymousSimilarityThreshold}
                      onChange={(e) =>
                        setLiveAnonymousSimilarityThreshold(e.target.value)
                      }
                      className="mt-1 w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            {!isRecording ? (
              <button
                onClick={startLiveTranscription}
                disabled={loading || !apiToken}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                🎙️ Demarrer la transcription live
              </button>
            ) : (
              <button
                onClick={stopLiveTranscription}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                ⏹️ Arreter l'enregistrement
              </button>
            )}
          </div>

          {/* Live Transcript Display */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 rounded-lg border border-blue-200 dark:border-blue-700 min-h-[120px]">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 flex items-center">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                Transcription live
              </h4>
              {liveTranscript &&
                liveUseSignatures &&
                hasSelectedVoiceSignatures && (
                  <div className="text-xs text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">
                    🎭 Voice ID actif
                  </div>
                )}
            </div>
            {liveTranscript ? (
              <div className="space-y-3">
                {/* Display transcript with proper line breaks for multiple speakers */}
                <div className="text-gray-800 dark:text-gray-200 leading-relaxed text-base space-y-2">
                  {liveTranscript.split("\n").map((line, index) => {
                    if (!line.trim()) return null;

                    // Parse speaker and text
                    const match = line.match(/^(.+?):\s*(.+)$/);
                    if (match) {
                      const [, speaker, text] = match;
                      return (
                        <div key={index} className="flex items-start space-x-2">
                          <span className="font-semibold text-blue-700 dark:text-blue-300 min-w-[100px]">
                            {speaker}:
                          </span>
                          <span className="flex-1">{text}</span>
                        </div>
                      );
                    }

                    return (
                      <div key={index} className="flex items-start">
                        <span>{line}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 italic flex items-center justify-between pt-2 border-t border-blue-200 dark:border-blue-700">
                  <span>
                    Derniere mise a jour: {new Date().toLocaleTimeString()}
                  </span>
                  {liveDiarization && (
                    <span className="bg-blue-100 dark:bg-blue-900 px-2 py-0.5 rounded">
                      🎯 Diarisation: ON
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-gray-500 dark:text-gray-400 italic text-center py-8">
                {isRecording ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Ecoute en cours...
                  </div>
                ) : (
                  "Cliquez sur 'Demarrer la transcription live' pour commencer"
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File Upload Testing */}
      <div className={glassPanelClass}>
        <div className="flex items-center gap-3 mb-1">
          <h3 className="text-lg font-semibold">
            📁 Transcription de fichier audio
          </h3>
          <span className="text-xs px-2 py-0.5 rounded-full border border-slate-400/30 bg-slate-700/40 text-slate-300 tracking-wide">
            Modele serveur
          </span>
        </div>
        <p className="text-sm text-white/40 mb-4">
          Le modele est configure cote serveur. La diarization necessite des
          signatures vocales generees dans la section ci-dessous.
        </p>

        <div className="space-y-4">
          {/* File Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Selectionner un fichier audio
            </label>
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {/* Voice Signatures for File Diarization */}
          <div>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={fileUseSignatures}
                onChange={(e) => setFileUseSignatures(e.target.checked)}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-white">
                  Activer la diarization des speakers
                  {hasGeneratedSignatures && fileUseSignatures && (
                    <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                      {selectedVoiceSignatures.length} speaker
                      {selectedVoiceSignatures.length === 1 ? "" : "s"}{" "}
                      selectionne(s)
                    </span>
                  )}
                </span>
                <p className="text-xs text-white/50 mt-0.5">
                  {fileUseSignatures && hasSelectedVoiceSignatures
                    ? `Identification: ${selectedVoiceSignatures.map((s) => s.name).join(", ")}`
                    : fileUseSignatures
                      ? "Mode anonyme : les locuteurs seront etiquetes Speaker 0, Speaker 1, ... (aucune signature selectionnee)"
                      : "Cochez pour activer la diarization (fonctionne avec ou sans signatures vocales)"}
                </p>
              </div>
            </label>

            {fileUseSignatures && (
              <div className="grid grid-cols-2 gap-3 pl-7 mt-3">
                <label className="block">
                  <span className="text-xs text-white/50">
                    Seuil signatures connues
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={fileVoiceSimilarityThreshold}
                    onChange={(e) =>
                      setFileVoiceSimilarityThreshold(e.target.value)
                    }
                    className="mt-1 w-full text-sm rounded border border-slate-500/40 bg-slate-800/60 text-white px-2 py-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-white/50">
                    Seuil regroupement anonyme
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={fileAnonymousSimilarityThreshold}
                    onChange={(e) =>
                      setFileAnonymousSimilarityThreshold(e.target.value)
                    }
                    className="mt-1 w-full text-sm rounded border border-slate-500/40 bg-slate-800/60 text-white px-2 py-1"
                  />
                </label>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={uploadAudioFile}
              disabled={loading || !apiToken || !selectedFile}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              🚀 Import synchrone
            </button>

            <button
              onClick={submitAsyncTranscription}
              disabled={loading || !apiToken || !selectedFile}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
            >
              ⏳ Import async en arriere-plan
            </button>
          </div>

          {selectedFile && (
            <div className="text-sm text-gray-600">
              Selectionne: {selectedFile.name} (
              {Math.round(selectedFile.size / 1024)} KB)
            </div>
          )}
        </div>
      </div>

      {/* Upload Results */}
      {uploadResults && (
        <div className={glassPanelClass}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-300/60 text-emerald-200 flex items-center justify-center text-xl">
                ✅
              </div>
              <h3 className="text-xl font-semibold text-white">
                Transcription terminee
              </h3>
            </div>
            {uploadResults.processing_time && (
              <span className="text-sm text-white/80 bg-white/10 px-3 py-1 rounded-full">
                ⏱️ {uploadResults.processing_time}s
              </span>
            )}
          </div>

          <div className="space-y-6">
            {(uploadResults.text || uploadResults.transcript) && (
              <div className="rounded-2xl border border-blue-300/30 bg-gradient-to-br from-blue-500/10 via-slate-900/40 to-transparent p-5">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  📝 Transcription complete
                </h4>
                <div className="text-white/80 leading-relaxed whitespace-pre-wrap">
                  {uploadResults.text || uploadResults.transcript}
                </div>
              </div>
            )}

            {(uploadResults.speakers && uploadResults.speakers.length > 0) ||
            (uploadResults.segments && uploadResults.segments.length > 0) ? (
              <div className="rounded-2xl border border-indigo-400/40 bg-gradient-to-br from-indigo-600/20 via-slate-950/50 to-transparent p-5">
                <h4 className="font-semibold text-white mb-4">
                  🎭 Segments de transcription (
                  {(uploadResults.speakers || uploadResults.segments).length})
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {(() => {
                    const segments =
                      uploadResults.speakers || uploadResults.segments;
                    const mergedSegments = [];

                    segments.forEach((segment) => {
                      const speakerLabel =
                        segment.speaker_name ||
                        (typeof segment.speaker === "string"
                          ? segment.speaker
                          : segment.speaker != null
                            ? `Speaker ${segment.speaker}`
                            : null);

                      if (
                        mergedSegments.length > 0 &&
                        mergedSegments[mergedSegments.length - 1]
                          .speaker_label === speakerLabel
                      ) {
                        const lastSegment =
                          mergedSegments[mergedSegments.length - 1];
                        lastSegment.text += " " + segment.text;
                        lastSegment.end = segment.end;
                        if (segment.words && lastSegment.words) {
                          lastSegment.words = lastSegment.words.concat(
                            segment.words,
                          );
                        }
                      } else {
                        mergedSegments.push({
                          ...segment,
                          speaker_label: speakerLabel,
                        });
                      }
                    });

                    return mergedSegments.map((segment, index) => (
                      <div
                        key={index}
                        className="rounded-xl border border-white/15 bg-white/5 backdrop-blur-xl p-3"
                      >
                        <div className="flex justify-between items-start mb-2">
                          {segment.speaker_label != null && (
                            <span className="font-medium text-white">
                              🗣️ {segment.speaker_label}
                            </span>
                          )}
                          <span className="text-xs text-white/70 bg-white/10 px-2 py-0.5 rounded">
                            {Math.round(segment.start)}s -{" "}
                            {Math.round(segment.end)}s
                          </span>
                        </div>
                        <p className="text-white/80 text-sm leading-relaxed">
                          "{segment.text}"
                        </p>
                        {segment.words && segment.words.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/10 text-xs text-white/60">
                            Nombre de mots: {segment.words.length} • Confiance
                            moyenne:{" "}
                            {Math.round(
                              (segment.words.reduce(
                                (sum, word) => sum + (word.probability || 1),
                                0,
                              ) /
                                segment.words.length) *
                                100,
                            )}
                            %
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3 pt-2 border-t border-white/10">
              {(uploadResults.text || uploadResults.transcript) && (
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(
                      uploadResults.text || uploadResults.transcript,
                    )
                  }
                  className="px-4 py-2 rounded-full bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 transition-colors text-sm"
                >
                  📋 Copier la transcription
                </button>
              )}
              <button
                onClick={() => setUploadResults(null)}
                className="px-4 py-2 rounded-full bg-white/5 text-white/80 hover:bg-white/10 transition-colors text-sm"
              >
                ❌ Effacer les resultats
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className={glassPanelClass}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Journal d'activite ({testResults.length})
          </h3>
          <button
            onClick={clearResults}
            className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-sm transition-colors"
          >
            🗑️ Tout effacer
          </button>
        </div>

        {testResults.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-2">📋</div>
            <p className="text-gray-500">Aucune activite pour l'instant</p>
            <p className="text-sm text-gray-400">
              Lancez des tests pour voir l'activite ici
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {testResults.map((result) => (
              <div
                key={result.id}
                className={`p-3 rounded-lg border-l-4 transition-all hover:shadow-sm ${
                  result.success
                    ? "border-green-400 bg-green-50 dark:bg-green-900/30"
                    : "border-red-400 bg-red-50 dark:bg-red-900/30"
                }`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-lg ${result.success ? "" : ""}`}>
                        {result.testName.includes("Live")
                          ? "🎤"
                          : result.testName.includes("Async")
                            ? "⏳"
                            : result.testName.includes("Sync")
                              ? "🚀"
                              : "🔧"}
                      </span>
                      <h4 className="font-medium text-gray-800 dark:text-gray-200">
                        {result.testName}
                      </h4>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          result.success
                            ? "bg-green-200 text-green-700 dark:bg-green-700 dark:text-green-200"
                            : "bg-red-200 text-red-700 dark:bg-red-700 dark:text-red-200"
                        }`}
                      >
                        {result.success ? "Succes" : "Echec"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {result.timestamp}{" "}
                      {result.duration && `• Duree: ${result.duration}`}
                    </div>
                  </div>
                </div>

                {result.error && (
                  <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/50 rounded text-sm">
                    <span className="text-red-700 dark:text-red-300">
                      ⚠️ {result.error}
                    </span>
                  </div>
                )}

                {result.data &&
                  typeof result.data === "string" &&
                  result.data.trim() && (
                    <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                      <span className="text-gray-700 dark:text-gray-300">
                        💬 "{result.data.substring(0, 100)}
                        {result.data.length > 100 ? "..." : ""}"
                      </span>
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedTranscriptionTesting;
